import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useAppStore from '../../store/appStore';
import {
  PencilIcon,
  TrashIcon,
  ResumeIcon,
  PauseIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '../../lib/commonUtils';
import { useLanguage } from '../../context/LanguageContext';
import {
  norm,
  clamp0to100,
  formatDate,
  normalizeAgent,
  getStatusColor,
  getTypeIcon,
  getTypeLabel,
  getIconBgColor,
  getIconTextColor,
  RefreshButton,
  PageHeader,
  SearchAndFilters,
  CustomDropdown,
  MagnifyingGlassIcon,
} from '../../lib/commonUtils';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DUMMY_AGENTS = [
  { agent_id: 'dummy-1', agent_name: 'SupportBot Alpha', agent_type: 'customer_support', is_active: true, total_calls: 284, success_rate: 91, average_call_duration: '3:42', created_at: '2025-09-01' },
  { agent_id: 'dummy-2', agent_name: 'LeadGen Pro', agent_type: 'lead_generation', is_active: true, total_calls: 195, success_rate: 78, average_call_duration: '5:15', created_at: '2025-10-14' },
  { agent_id: 'dummy-3', agent_name: 'Scheduler Bot', agent_type: 'appointment_scheduling', is_active: false, total_calls: 130, success_rate: 85, average_call_duration: '4:10', created_at: '2025-11-03' },
  { agent_id: 'dummy-4', agent_name: 'Retention Agent', agent_type: 'customer_support', is_active: true, total_calls: 210, success_rate: 88, average_call_duration: '6:05', created_at: '2025-08-20' },
  { agent_id: 'dummy-5', agent_name: 'Outreach Bot', agent_type: 'lead_generation', is_active: true, total_calls: 320, success_rate: 72, average_call_duration: '2:55', created_at: '2025-12-01' },
];

const AgentList = () => {
  const { t, locale } = useLanguage();
  const [searchParams] = useSearchParams();
  const fromUser = searchParams.get('fromUser') === 'true';
  const urlEmail = (searchParams.get('email') || '').trim();

  const { user: currentUser } = useAuthStore();
  const currentEmail = (currentUser?.email || currentUser?.user_email || '').trim();
  const currentEmailLc = currentEmail.toLowerCase();
  const targetEmail = (fromUser && urlEmail) ? urlEmail : (urlEmail || currentEmail || '');

  const {
    agents: rawAgents,
    adminUserInfo,
    agentsLoading,
    agentsLoaded,
    setAgentsScope,
    loadAgentsForCurrentScope,
    fetchDashboardAnalysis,
    deleteAgent,
    pauseAgent,
    resumeAgent,
  } = useAppStore();
  const loading = agentsLoading;

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');
  const [statsById, setStatsById] = useState(() => new Map());
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(null); // Track which agent is toggling status

  const fetchAgentStats = useCallback(async () => {
    const isDummy = (() => { try { return localStorage.getItem('token') === 'dummy-token-valoria-demo'; } catch { return false; } })();
    if (isDummy) {
      const map = new Map();
      DUMMY_AGENTS.forEach((r) => {
        const key = String(r?.agent_id || r?.id || '').trim();
        if (key) map.set(key, r);
      });
      setStatsById(map);
      setStatsLoading(false);
      return;
    }
    setStatsLoading(true);
    setStatsError('');
    try {
      // Use cached store data — no extra network request
      const result = await fetchDashboardAnalysis();
      const rows = result?.data?.your_ai_agents?.test_speed || [];
      const map = new Map();
      rows.forEach((r) => {
        const key = String(r?.agent_id || '').trim();
        if (key) map.set(key, {
          agent_id: r.agent_id,
          total_calls: r.calls ?? 0,
          success_rate_value: r.success_rate ?? 0,
          average_call_duration_seconds: 0,
        });
      });
      setStatsById(map);
    } catch (e) {
      setStatsError(e?.response?.data?.message || e?.message || t('failedToLoadAgentStats'));
      setStatsById(new Map());
    } finally {
      setStatsLoading(false);
    }
  }, [fetchDashboardAnalysis]);

  useEffect(() => {
    if (!targetEmail) return;
    setStatsError('');
    const scope = (fromUser && urlEmail)
      ? { type: 'user', email: urlEmail }
      : { type: 'self', email: null };
    setAgentsScope(scope);
    loadAgentsForCurrentScope().then((r) => {
      if (!r?.success && !r?.canceled) {
        // Handle error if needed
      }
    });
    fetchAgentStats();
  }, [targetEmail, setAgentsScope, loadAgentsForCurrentScope, fetchAgentStats]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState('');

  const rawByAnyId = useMemo(() => {
    const map = new Map();
    (rawAgents || []).forEach((a) => {
      if (a?.id != null) map.set(String(a.id), a);
      if (a?.agent_id) map.set(String(a.agent_id), a);
    });
    return map;
  }, [rawAgents]);

  const scopedRawAgents = useMemo(() => {
    if (fromUser && urlEmail) {
      const matchLc = urlEmail.toLowerCase();
      return (rawAgents || []).filter(a => String(a?.user_email || '').toLowerCase() === matchLc);
    }
    return rawAgents || [];
  }, [fromUser, urlEmail, rawAgents]);

  const agents = useMemo(() => {
    return (scopedRawAgents || []).map(raw => normalizeAgent(raw, t, locale));
  }, [scopedRawAgents, t, locale]);

  const filteredAgents = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return (agents || []).filter((a) => {
      const nameMatch = (a.name || a.agent_name || '').toLowerCase().includes(lower);
      const agentType = a.agent_type || a.type || '';
      const typeMatch = filterType === 'all' || agentType === norm(filterType);
      const statusMatch =
        filterStatus === 'all'
          ? true
          : filterStatus === 'Active'
            ? a.is_active === true
            : filterStatus === 'Inactive'
              ? a.is_active === false
              : true;
      return nameMatch && typeMatch && statusMatch;
    });
  }, [agents, searchTerm, filterType, filterStatus]);

  const doRefresh = useCallback(async () => {
    if (!targetEmail) return;
    setError('');
    const [r1] = await Promise.allSettled([
      loadAgentsForCurrentScope(),
      (async () => { try { await fetchAgentStats(); } catch { } })(),
    ]);
    if (r1.status === 'fulfilled') {
      const r = r1.value;
      if (!r?.success && !r?.canceled) setError(r?.error || t('errorFetchingAgents'));
    } else {
      setError(t('errorFetchingAgents'));
    }
  }, [loadAgentsForCurrentScope, targetEmail, fetchAgentStats]);

  const getOwnerEmailFromRaw = (agent) => {
    const key = String(agent?.id ?? agent?.agent_id ?? '');
    const raw = rawByAnyId.get(key);
    return String(raw?.user_email || raw?.owner_email || raw?.email || '').toLowerCase();
  };

  const canEdit = (agent) => {
    const ownerLc = getOwnerEmailFromRaw(agent);
    return ownerLc && ownerLc === currentEmailLc;
  };

  const handleDeleteClick = (agent) => {
    setAgentToDelete(agent);
  };

  const closeDeleteModal = () => {
    setAgentToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;

    setIsDeleting(true);
    try {
      const agentId = agentToDelete?.id ?? agentToDelete?.agent_id;
      const r = await deleteAgent(agentId);
      if (!r.success) {
        toast.error(r.error || t('errorDeletingAgent'), {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }

      toast.success(t('agentDeletedSuccess'), {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      closeDeleteModal();
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePauseToggle = async (agent) => {
    const id = agent?.id ?? agent?.agent_id;
    if (!id) return;

    setIsTogglingStatus(id);

    try {
      const r = agent.is_active ? await pauseAgent(id) : await resumeAgent(id);

      if (r.success) {
        toast.success(t('agentStatusUpdated'), {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // REMOVED: await refetchScoped();
        // The Zustand store already updates the agents array optimistically
        // So the UI will update automatically

      } else {
        let errorMsg = r.error || t('errorUpdatingStatus');

        if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
          errorMsg = 'Cannot resume agent. You have exhausted your call minutes (301.00/300). Please upgrade your plan to continue.';
        }

        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

      }
    } catch (error) {
      let errorMsg = error.message || t('errorUpdatingStatus');

      if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
        errorMsg = 'Cannot resume agent. You have exhausted your call minutes (301.00/300). Please upgrade your plan to continue.';
      }

      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } finally {
      setIsTogglingStatus(null);
    }
  };

  const isListLoading = loading || !agentsLoaded;
  const isBusy = isListLoading || statsLoading;

  const formatDuration = (seconds) => {
    const sec = Number(seconds) || 0;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('aiAgents')}
        subtitle={
          targetEmail && adminUserInfo
            ? `${t('viewingAgentsFor')} ${adminUserInfo.user_name || '—'} (${adminUserInfo.user_email || targetEmail})`
            : t('manageAgents')
        }
      >
        <div className="grid grid-cols-2 gap-3 w-full sm:w-auto mt-4 sm:mt-0">
          <RefreshButton onClick={doRefresh} isLoading={isBusy} className="btn-secondary w-full sm:w-auto" />
          {(!fromUser || (adminUserInfo?.user_email && adminUserInfo.user_email.toLowerCase() === currentEmailLc)) && (
            <Link
              to="/agents/create"
              className="btn btn-primary w-full sm:w-auto"
            >
              <PlusIcon className="h-4 w-4" />
              {t('createAgent')}
            </Link>
          )}
        </div>
      </PageHeader>

      <div className="lg:hidden p-4 controlled-glass rounded-[24px] mb-6 inner-highlight relative z-[20]">
        <div className="space-y-4">
          <div className="relative group">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/20 group-focus-within:text-white transition-colors" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchAgents')}
              disabled={isListLoading}
              className="input pl-11"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CustomDropdown
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              options={[
                { value: 'all', label: t('allTypes') },
                { value: 'customer_support', label: t('support') },
                { value: 'lead_generation', label: t('leads') },
                { value: 'appointment_scheduling', label: t('appts') },
              ]}
              placeholder={t('allTypes')}
              className="text-sm"
            />
            <CustomDropdown
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              options={[
                { value: 'all', label: t('allStatus') },
                { value: 'Active', label: t('active') },
                { value: 'Inactive', label: t('inactive') },
              ]}
              placeholder={t('allStatus')}
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <div className="hidden sm:block">
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          searchPlaceholder={t('searchAgents')}
          t={t}
          disabled={isListLoading}
          filters={[
            {
              value: filterType,
              onChange: (e) => setFilterType(e.target.value),
              options: [
                { value: 'all', label: t('allTypes') },
                { value: 'customer_support', label: t('customer_support') },
                { value: 'lead_generation', label: t('lead_generation') },
                { value: 'appointment_scheduling', label: t('appointment_scheduling') },
              ],
            },
            {
              value: filterStatus,
              onChange: (e) => setFilterStatus(e.target.value),
              options: [
                { value: 'all', label: t('allStatus') },
                { value: 'Active', label: t('active') },
                { value: 'Inactive', label: t('inactive') },
              ],
            },
          ]}
        />
      </div>

      {(error || statsError) && (
        <div className="controlled-glass p-4 rounded-2xl border border-red-500/20 text-sm text-red-400 font-medium inner-highlight">
          {error || statsError}
        </div>
      )}

      {isListLoading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="controlled-glass p-8 animate-pulse rounded-[32px] inner-highlight">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <div className="h-14 w-14 rounded-2xl bg-white/5" />
                  <div className="ml-4">
                    <div className="h-4 w-32 bg-white/10 rounded-full" />
                    <div className="h-2 w-20 bg-white/5 rounded-full mt-2" />
                  </div>
                </div>
                <div className="h-6 w-16 bg-white/5 rounded-full" />
              </div>
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="h-12 w-full bg-white/5 rounded-xl" />
                <div className="h-12 w-full bg-white/5 rounded-xl" />
                <div className="h-12 w-full bg-white/5 rounded-xl" />
              </div>
              <div className="h-2 w-32 bg-white/5 rounded-full mb-8" />
              <div className="flex justify-between items-center pt-4 border-t border-white/5">
                <div className="flex space-x-3">
                  <div className="h-8 w-8 bg-white/5 rounded-lg" />
                  <div className="h-8 w-8 bg-white/5 rounded-lg" />
                </div>
                <div className="h-10 w-24 bg-white/10 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredAgents.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 pb-8">
          {filteredAgents.map((agent) => {
            const id = agent?.id ?? agent?.agent_id;
            const name = agent.name;
            const type = agent.type;

            const stat = statsById.get(String(agent?.agent_id || agent?.id || '')) || {};
            const total_calls = (Number.isFinite(Number(stat.total_calls)) ? Number(stat.total_calls) : agent.total_calls) ?? 0;
            const success_rate_value = (Number.isFinite(Number(stat.success_rate_value)) ? Number(stat.success_rate_value) : clamp0to100(agent.success_rate)) ?? 0;
            const avg_secs =
              Number.isFinite(Number(stat.average_call_duration_seconds))
                ? Number(stat.average_call_duration_seconds)
                : 0;
            const average_call_duration =
              avg_secs > 0 ? formatDuration(avg_secs) : (agent.average_call_duration || '0:00');

            const created_at =
              agent.created_at ||
              stat.created_at ||
              null;

            const allowEdit = canEdit(agent);
            const isStatusLoading = isTogglingStatus === id;

            return (
              <div key={id} className="controlled-glass rounded-3xl sm:rounded-xl inner-highlight group hover:border-white/20 transition-all duration-300 flex flex-col overflow-hidden">

                {/* ── Card Header ── */}
                <div className="p-6 pb-4 sm:p-4 sm:pb-3">
                  <div className="flex items-start justify-between gap-3">
                    {/* Icon + Name */}
                    <div className="flex items-center gap-4 sm:gap-3 min-w-0">
                      <div className={`flex-shrink-0 p-3.5 sm:p-2.5 ${getIconBgColor(type)} rounded-2xl sm:rounded-xl border border-white/8 shadow-lg`}>
                        <div className={`${getIconTextColor(type)} sm:scale-90`}>
                          {getTypeIcon(type)}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-[15px] sm:text-[14px] font-bold text-white truncate leading-tight tracking-tight">{name}</h3>
                        <p className="text-[10px] sm:text-[9px] font-semibold text-white/30 uppercase tracking-[0.12em] mt-1">{getTypeLabel(type, t)}</p>
                      </div>
                    </div>
                    {/* Status Badge */}
                    <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-2.5 sm:py-1 rounded-full text-[9px] sm:text-[8px] font-bold uppercase tracking-widest border ${agent.is_active
                      ? 'bg-green-500/10 text-green-400 border-green-500/25'
                      : 'bg-red-500/10 text-red-400 border-red-500/25'
                      }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${agent.is_active ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                      {agent.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* ── Stats Grid ── */}
                <div className="px-6 pb-5 sm:px-4 sm:pb-4">
                  <div className="grid grid-cols-3 gap-2.5 sm:gap-2">
                    {[
                      { value: total_calls, label: t('calls') },
                      { value: `${clamp0to100(success_rate_value)}%`, label: t('success') },
                      { value: average_call_duration, label: t('avgDur') },
                    ].map(({ value, label }) => (
                      <div key={label} className="text-center py-4 px-2 sm:py-3 sm:px-1 bg-white/[0.04] rounded-2xl sm:rounded-xl border border-white/[0.06] group-hover:bg-white/[0.07] transition-colors">
                        <div className="text-2xl sm:text-lg font-bold text-white tracking-tight leading-none">{value}</div>
                        <div className="text-[9px] sm:text-[8px] font-bold text-white/25 uppercase tracking-[0.12em] mt-2 sm:mt-1.5">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Success Rate Progress ── */}
                <div className="px-6 pb-5 sm:px-4 sm:pb-3">
                  <div className="flex justify-between items-center mb-2 sm:mb-1">
                    <span className="text-[9px] sm:text-[8px] font-bold text-white/25 uppercase tracking-widest">Success Rate</span>
                    <span className="text-[10px] sm:text-[8px] font-bold text-primary-400">{clamp0to100(success_rate_value)}%</span>
                  </div>
                  <div className="h-1 w-full bg-white/[0.05] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary-600 to-primary-400 rounded-full transition-all duration-1000"
                      style={{ width: `${clamp0to100(success_rate_value)}%`, boxShadow: '0 0 6px rgba(79,110,247,0.4)' }}
                    />
                  </div>
                </div>

                {/* ── Created At ── */}
                <div className="px-6 pb-5 sm:px-4 sm:pb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/15" />
                    <span className="text-[9px] sm:text-[8px] text-white/20 font-semibold uppercase tracking-widest">
                      {t('created')}: {created_at ? formatDate(created_at, locale) : '—'}
                    </span>
                  </div>
                </div>

                {/* ── Action Footer ── */}
                <div className="mt-auto px-6 py-4 sm:px-4 sm:py-3 border-t border-white/[0.06] flex items-center justify-between gap-3">
                  {/* Icon Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={allowEdit ? `/agents/${id}/edit` : '#'}
                      onClick={(e) => { if (!allowEdit) e.preventDefault(); }}
                      className={`p-2.5 sm:p-2 rounded-xl border transition-all ${allowEdit
                        ? 'text-white/35 border-white/10 hover:text-white hover:bg-white/10 hover:border-white/20'
                        : 'text-white/10 border-white/5 cursor-not-allowed'
                        }`}
                      title={allowEdit ? 'Edit Agent' : 'Editing disabled'}
                    >
                      <PencilIcon className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                    </Link>
                    <button
                      onClick={() => handleDeleteClick(agent)}
                      className="p-2.5 sm:p-2 text-white/35 border border-white/10 hover:text-red-400 hover:bg-red-400/10 hover:border-red-400/20 rounded-xl transition-all"
                      title="Delete Agent"
                    >
                      <TrashIcon className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                    </button>
                  </div>

                  {/* Status Toggle */}
                  {agent.is_active ? (
                    <button
                      onClick={() => handlePauseToggle(agent)}
                      disabled={isStatusLoading}
                      className={`btn text-[10px] sm:text-[9px] font-bold uppercase tracking-widest px-5 py-2.5 sm:px-3 sm:py-1.5 rounded-xl sm:rounded-lg bg-white/5 border border-white/15 hover:bg-white/10 hover:border-white/25 transition-all ${isStatusLoading ? 'opacity-50' : ''
                        }`}
                    >
                      <PauseIcon className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                      <span>{isStatusLoading ? 'Pausing…' : 'Pause'}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePauseToggle(agent)}
                      disabled={isStatusLoading}
                      className={`btn btn-primary text-[10px] sm:text-[9px] font-bold uppercase tracking-widest px-5 py-2.5 sm:px-3 sm:py-1.5 rounded-xl sm:rounded-lg transition-all ${isStatusLoading ? 'opacity-50' : ''
                        }`}
                    >
                      <ResumeIcon className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                      <span>{isStatusLoading ? 'Resuming…' : 'Resume'}</span>
                    </button>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      ) : (
        <div className="controlled-glass py-24 px-8 text-center rounded-[40px] inner-highlight">
          <div className="p-6 bg-white/5 border border-white/10 rounded-[32px] w-fit mx-auto mb-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-white/20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a9 9 0 00-9 9v2a9 9 0 009 9 9 9 0 009-9v-2a9 9 0 00-9-9z" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 15c1.333 1 2.667 1 4 0" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white tracking-tight">{t('noAgentsFound')}</h3>
          {targetEmail ? (
            <p className="mt-2 text-sm font-medium text-white/30 italic max-w-md mx-auto">
              No agents for <span className="text-white/60 font-bold">{targetEmail}</span>.
            </p>
          ) : (
            <p className="mt-2 text-sm font-medium text-white/30 italic max-w-md mx-auto">
              You haven't created any AI agents yet. You can{' '}
              <Link to="/agents/create" className="text-primary-400 hover:text-primary-300 font-bold border-b border-primary-500/30 hover:border-primary-400">
                create an agent
              </Link>{' '}
              to get started.
            </p>
          )}
        </div>
      )}

      {agentToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div
            className="fixed inset-0 bg-slate-950/40 backdrop-blur-md"
            onClick={closeDeleteModal}
          />
          <div className="relative controlled-glass rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden inner-highlight">
            <div className="p-10 text-center">
              <div className="mx-auto w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-[32px] flex items-center justify-center mb-8">
                <ExclamationTriangleIcon className="h-10 w-10 text-red-500" />
              </div>
              <h3 className="text-2xl font-bold text-white tracking-tight mb-2">Delete Agent</h3>
              <p className="text-white/40 mb-10 font-medium italic">Are you sure you want to delete <span className="text-white not-italic font-bold">"{agentToDelete.name}"</span>? This action cannot be undone.</p>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={closeDeleteModal}
                  className="btn bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-2xl sm:rounded-xl py-4 sm:py-2 text-xs font-bold uppercase tracking-widest transition-all"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="btn bg-red-600 border border-red-500 text-white rounded-2xl sm:rounded-xl py-4 sm:py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-500 transition-all shadow-lg shadow-red-600/20"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Agent'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentList;